function updateNestedObjectHandler<T>({
  obj,
  path,
  value,
}: {
  obj: T;
  path: Array<string>;
  value: { [key: string | number]: unknown } | unknown;
}) {
  const idx = path[0];

  if (!idx) {
    return obj;
  }

  let tempObj = obj;
  const isLast = path.length === 1;

  if (!isLast && !tempObj[idx]) {
    tempObj = {
      ...tempObj,
      [idx]: {},
    };
  }

  if (isLast) {
    if (typeof value === 'object') {
      tempObj = {
        ...tempObj,
        [idx]: { ...tempObj[idx], ...value },
      };
    } else {
      tempObj = {
        ...tempObj,
        [idx]: value,
      };
    }
  } else {
    path.shift();

    tempObj[idx] = updateNestedObjectHandler({
      obj: tempObj[idx],
      path: path,
      value,
    });
  }

  return tempObj;
}

export function updateNestedObject<T>({
  obj,
  path,
  value,
}: {
  obj: T;
  path?: string;
  value?: { [key: string | number]: unknown } | unknown;
}) {
  if (!path || !value) return obj;

  path = path
    .replaceAll('!', '')
    .replaceAll('?', '')
    .replaceAll('[', '.')
    .replaceAll(']', '.')
    .replaceAll('..', '.');
  path = path.endsWith('.') ? path.slice(0, -1) : path;
  path = path.startsWith('.') ? path.slice(1) : path;

  return updateNestedObjectHandler<T>({
    obj,
    path: path.split('.'),
    value,
  }) as T;
}

/**
 * Safely gets a nested property from an object using a dot-separated path.
 * Returns undefined if any part of the path does not exist.
 *
 * @param obj The object to query.
 * @param path The dot-separated path string (e.g., 'user.address.city').
 * @returns The value at the specified path, or undefined if not found.
 */
export function getPropByPath<T, K>(obj: T, path: string): K | undefined {
  // Check if the object is null or undefined
  if (obj === null || typeof obj === 'undefined') {
    return undefined;
  }

  // Split the path into an array of keys
  const keys = path.split('.');

  // Use a reduce function to safely traverse the object
  let current: any = obj; // Start with the initial object

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];

    // Check if the current level is an object and contains the key
    if (current !== null && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      // If any part of the path is missing or not an object, return undefined
      return undefined;
    }
  }

  // Return the final value found at the end of the path
  return current as K;
}
